import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { MapPin, Search, ChevronDown, ChevronUp, User, Phone, Mail, Briefcase, FileText, DollarSign } from 'lucide-react';

const PropertiesManagement = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProperty, setExpandedProperty] = useState(null);

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    setLoading(true);
    try {
      // Get all jobs with their customer and estimate information
      const { data: jobsData, error: jobsError } = await supabase
        .from('jobs')
        .select(`
          id,
          job_number,
          job_address,
          job_city,
          job_province,
          job_postal_code,
          status,
          start_date,
          completion_date,
          total_amount,
          customer_id,
          estimate_id,
          customers (
            id,
            first_name,
            last_name,
            company,
            phone,
            email
          )
        `)
        .order('job_address', { ascending: true });

      if (jobsError) throw jobsError;

      // Get all estimates
      const { data: estimatesData, error: estimatesError } = await supabase
        .from('estimates')
        .select(`
          id,
          estimate_number,
          job_address,
          job_city,
          job_province,
          job_postal_code,
          status,
          total_amount,
          created_at,
          customer_id,
          customers (
            id,
            first_name,
            last_name,
            company,
            phone,
            email
          )
        `)
        .order('job_address', { ascending: true });

      if (estimatesError) throw estimatesError;

      // Get all invoices
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          invoice_date,
          total_amount,
          status,
          job_id
        `);

      if (invoicesError) throw invoicesError;

      // Group by property address
      const propertyMap = {};

      // Process jobs
      (jobsData || []).forEach(job => {
        const address = `${job.job_address}, ${job.job_city}, ${job.job_province} ${job.job_postal_code}`.trim();
        if (!propertyMap[address]) {
          propertyMap[address] = {
            address: job.job_address,
            city: job.job_city,
            province: job.job_province,
            postalCode: job.job_postal_code,
            fullAddress: address,
            customers: new Map(),
            jobs: [],
            estimates: [],
            invoices: []
          };
        }

        // Add customer
        if (job.customers) {
          propertyMap[address].customers.set(job.customer_id, job.customers);
        }

        // Add job
        propertyMap[address].jobs.push(job);

        // Add related invoices
        const relatedInvoices = (invoicesData || []).filter(inv => inv.job_id === job.id);
        propertyMap[address].invoices.push(...relatedInvoices);
      });

      // Process estimates (only those without associated jobs)
      (estimatesData || []).forEach(estimate => {
        const address = `${estimate.job_address}, ${estimate.job_city}, ${estimate.job_province} ${estimate.job_postal_code}`.trim();

        if (!propertyMap[address]) {
          propertyMap[address] = {
            address: estimate.job_address,
            city: estimate.job_city,
            province: estimate.job_province,
            postalCode: estimate.job_postal_code,
            fullAddress: address,
            customers: new Map(),
            jobs: [],
            estimates: [],
            invoices: []
          };
        }

        // Add customer
        if (estimate.customers) {
          propertyMap[address].customers.set(estimate.customer_id, estimate.customers);
        }

        // Add estimate
        propertyMap[address].estimates.push(estimate);
      });

      // Convert to array and calculate totals
      const propertiesArray = Object.values(propertyMap).map(prop => ({
        ...prop,
        customers: Array.from(prop.customers.values()),
        totalJobs: prop.jobs.length,
        totalEstimates: prop.estimates.length,
        totalRevenue: prop.jobs.reduce((sum, job) => sum + (parseFloat(job.total_amount) || 0), 0),
        lastActivity: getLastActivity(prop)
      }));

      setProperties(propertiesArray);
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLastActivity = (property) => {
    const dates = [];

    property.jobs.forEach(job => {
      if (job.completion_date) dates.push(new Date(job.completion_date));
      else if (job.start_date) dates.push(new Date(job.start_date));
    });

    property.estimates.forEach(est => {
      if (est.created_at) dates.push(new Date(est.created_at));
    });

    if (dates.length === 0) return null;
    return new Date(Math.max(...dates));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Completed': 'bg-green-100 text-green-800',
      'In Progress': 'bg-blue-100 text-blue-800',
      'Scheduled': 'bg-yellow-100 text-yellow-800',
      'Cancelled': 'bg-red-100 text-red-800',
      'Pending': 'bg-gray-100 text-gray-800',
      'Sent': 'bg-purple-100 text-purple-800',
      'Paid': 'bg-green-100 text-green-800',
      'Overdue': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredProperties = properties.filter(prop =>
    prop.fullAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prop.customers.some(c =>
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.company || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading properties...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Property & Job Site Management</h1>
          <p className="text-gray-600 mt-1">View all properties where work has been performed or estimated</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search by address or customer name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Properties</p>
              <p className="text-2xl font-bold text-gray-900">{properties.length}</p>
            </div>
            <MapPin className="text-blue-500" size={32} />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Jobs</p>
              <p className="text-2xl font-bold text-gray-900">
                {properties.reduce((sum, p) => sum + p.totalJobs, 0)}
              </p>
            </div>
            <Briefcase className="text-green-500" size={32} />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Estimates</p>
              <p className="text-2xl font-bold text-gray-900">
                {properties.reduce((sum, p) => sum + p.totalEstimates, 0)}
              </p>
            </div>
            <FileText className="text-purple-500" size={32} />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(properties.reduce((sum, p) => sum + p.totalRevenue, 0))}
              </p>
            </div>
            <DollarSign className="text-yellow-500" size={32} />
          </div>
        </div>
      </div>

      {/* Properties List */}
      <div className="space-y-4">
        {filteredProperties.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <MapPin className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Properties Found</h3>
            <p className="text-gray-500">
              {searchTerm ? 'Try adjusting your search criteria' : 'No job sites or properties in the system yet'}
            </p>
          </div>
        ) : (
          filteredProperties.map((property, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Property Header */}
              <div
                className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedProperty(expandedProperty === index ? null : index)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-start space-x-3">
                      <MapPin className="text-blue-600 mt-1 flex-shrink-0" size={24} />
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{property.address}</h3>
                        <p className="text-gray-600">{property.city}, {property.province} {property.postalCode}</p>

                        {/* Customer Info */}
                        <div className="mt-3 flex flex-wrap gap-4">
                          {property.customers.map((customer, idx) => (
                            <div key={idx} className="flex items-center space-x-2 text-sm">
                              <User className="text-gray-400" size={16} />
                              <span className="text-gray-700">
                                {customer.company || `${customer.first_name} ${customer.last_name}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-6 ml-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Jobs</p>
                      <p className="text-xl font-semibold text-gray-900">{property.totalJobs}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Revenue</p>
                      <p className="text-xl font-semibold text-green-600">{formatCurrency(property.totalRevenue)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Last Activity</p>
                      <p className="text-sm text-gray-700">{formatDate(property.lastActivity)}</p>
                    </div>
                    {expandedProperty === index ? (
                      <ChevronUp className="text-gray-400" size={24} />
                    ) : (
                      <ChevronDown className="text-gray-400" size={24} />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedProperty === index && (
                <div className="border-t border-gray-200 bg-gray-50 p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Customer Details */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <User className="mr-2" size={18} />
                        Customer Information
                      </h4>
                      <div className="space-y-2">
                        {property.customers.map((customer, idx) => (
                          <div key={idx} className="bg-white rounded p-3 space-y-1">
                            <p className="font-medium text-gray-900">
                              {customer.company || `${customer.first_name} ${customer.last_name}`}
                            </p>
                            {customer.phone && (
                              <p className="text-sm text-gray-600 flex items-center">
                                <Phone size={14} className="mr-2" />
                                {customer.phone}
                              </p>
                            )}
                            {customer.email && (
                              <p className="text-sm text-gray-600 flex items-center">
                                <Mail size={14} className="mr-2" />
                                {customer.email}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Jobs History */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <Briefcase className="mr-2" size={18} />
                        Jobs History ({property.jobs.length})
                      </h4>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {property.jobs.length === 0 ? (
                          <p className="text-gray-500 text-sm">No jobs at this location</p>
                        ) : (
                          property.jobs.map((job) => (
                            <div key={job.id} className="bg-white rounded p-3">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-gray-900">Job #{job.job_number}</p>
                                  <p className="text-sm text-gray-600">
                                    {formatDate(job.start_date)} - {formatDate(job.completion_date)}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-semibold text-gray-900">{formatCurrency(job.total_amount)}</p>
                                  <span className={`inline-block px-2 py-1 text-xs rounded ${getStatusColor(job.status)}`}>
                                    {job.status}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Estimates History */}
                    {property.estimates.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <FileText className="mr-2" size={18} />
                          Estimates ({property.estimates.length})
                        </h4>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {property.estimates.map((estimate) => (
                            <div key={estimate.id} className="bg-white rounded p-3">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-gray-900">Estimate #{estimate.estimate_number}</p>
                                  <p className="text-sm text-gray-600">{formatDate(estimate.created_at)}</p>
                                </div>
                                <div className="text-right">
                                  <p className="font-semibold text-gray-900">{formatCurrency(estimate.total_amount)}</p>
                                  <span className={`inline-block px-2 py-1 text-xs rounded ${getStatusColor(estimate.status)}`}>
                                    {estimate.status}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Invoices */}
                    {property.invoices.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <DollarSign className="mr-2" size={18} />
                          Invoices ({property.invoices.length})
                        </h4>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {property.invoices.map((invoice) => (
                            <div key={invoice.id} className="bg-white rounded p-3">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-gray-900">Invoice #{invoice.invoice_number}</p>
                                  <p className="text-sm text-gray-600">{formatDate(invoice.invoice_date)}</p>
                                </div>
                                <div className="text-right">
                                  <p className="font-semibold text-gray-900">{formatCurrency(invoice.total_amount)}</p>
                                  <span className={`inline-block px-2 py-1 text-xs rounded ${getStatusColor(invoice.status)}`}>
                                    {invoice.status}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PropertiesManagement;
